/* Copyright (c) 2020-present CloudIO Inc */

import { useStateX } from '@cloudio-saas/statex';
import { ExpressionArrayProp, ExpressionValue } from 'componentregistry';
import { isMultiOperator } from 'components/SmartSearch/SearchUtils';
import { designPath } from 'designer/atoms';
import React, { memo, useEffect } from 'react';
import ExpressionProperty from './ExpressionProperty';
import { getDefaultValues } from './PropertyEditorUtils';
import { PropertyEditorProps } from './types';

function ExpressionArrayProperty({
  activeItemId,
  def,
  mgr,
  pageId,
  property,
  onChange,
  appUid,
  value = getDefaultValues<ExpressionValue[]>(mgr, property),
  hmr,
  platform,
  isViewOnlyUser = false,
}: PropertyEditorProps<ExpressionArrayProp, ExpressionValue[]>) {
  const [operator] = useStateX<string>(
    designPath({
      appUid,
      pageId,
      itemId: activeItemId,
      key: 'props',
      subkey: 'operator',
    }),
    '',
  );
  const [hasTime] = useStateX<boolean>(
    designPath({
      appUid,
      pageId,
      itemId: activeItemId,
      key: 'props',
      subkey: 'hasTime',
    }),
    false,
  );
  useEffect(() => {
    if (operator === 'bn') {
      if (!value?.[1]) {
        const newValue = [...value];
        newValue[1] = { type: 'text', value: '' };
        onChange(property, newValue);
      } else if (value.length > 2) {
        const newValue = value.slice(0, 2);
        onChange(property, newValue);
      }
    } else if (!isMultiOperator(operator) && value.length > 1) {
      const newValue = value.slice(0, 1);
      onChange(property, newValue);
    }
  }, [onChange, operator, property, value]);

  const isMultipOp = isMultiOperator(operator);

  if (!Array.isArray(value)) value = [value];

  return (
    <>
      {value.map((v, index) => (
        <ExpressionProperty
          isViewOnlyUser={isViewOnlyUser}
          // eslint-disable-next-line react/no-array-index-key
          key={`i${index}`}
          data-testid={property}
          activeItemId={activeItemId}
          def={{
            ...def,
            type: 'expression',
            onAdd: isMultipOp
              ? () => {
                  const newValue = [...value];
                  newValue.splice(index + 1, 0, { type: 'text', value: '' });
                  onChange(property, newValue);
                }
              : undefined,
            onRemove:
              index > 0 && isMultipOp
                ? () => {
                    const newValue = [...value];
                    newValue.splice(index, 1);
                    onChange(property, newValue);
                  }
                : undefined,
            hasTime,
          }}
          mgr={mgr}
          pageId={pageId}
          appUid={appUid}
          property={`${property}-${index}`}
          value={v as ExpressionValue}
          onChange={(ind, val) => {
            if (val) {
              const newValue = [...value];
              newValue[index] = val;
              onChange(property, newValue);
            }
          }}
          hmr={hmr}
          platform={platform}
        />
      ))}
    </>
  );
}

export default memo(ExpressionArrayProperty);
